import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Collapse, Divider, Grid, Paper, Typography } from '@mui/material';
import PatientSummary from './patient-summary/patient-summary.component';
import DeliverySummary from './delivery-summary/delivery-summary.component';
import SampleSummary from './sample-summary/sample-summary.component';
import DentistSummary from './dentist-summary/dentist-summary.component';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import FileGrid from 'components/shared/file-upload-area/file-grid.component';
import { OrderInputType } from 'models/order-input/shared';
import { ActionOrderContext } from 'pages/order-flow/action-order/action-order-context';
import { TransitionGroup } from 'react-transition-group';
import ConstructionBox from '../constructions-step/constructions/construction-box.component';
import SupplierSummary from 'components/order-flow/supplier-order/confirm-step/supplier-summary/supplier-summary.component';
import PriceSpecificationSection from './price-specification-section/price-specification-section.componet';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useMutation } from '@apollo/client';
import { createOrderTemplate, modifyOrderTemplate, saveCompletedDraftTemplate } from 'apollo/templates/order';
import { actionOrderToOrderInput } from 'components/order-flow/shared/order-input-helper';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { customerSettings } from 'utils/customerSettings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import { OrderStatus } from 'models/order';
import { getTreatingClinicianTitleWords, isTreatingClinicianTitle } from 'utils/treating-clinician';

type ConfirmStepProps = {
    onBackClick: () => void;
};

const ConfirmStep: FC<ConfirmStepProps> = ({ onBackClick }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order } = useContext(ActionOrderContext);
    const [isResend, setIsResend] = useState(false);
    const search = useLocation().search;
    const navigate = useNavigate();
    const currentUser = currentUserVar();
    const currentOrg = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const isCompletedDraftEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.COMPLETED_DRAFT,
    );

    const shouldDisallowNonDentists = customerSettings.getCustomerSetting(
        currentOrg.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.DISALLOW_NON_DENTIST,
    );

    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        setIsResend(queryParams.get('isResend') === 'true' || false);
    }, [search]);

    const [createOrder, { loading }] = useMutation(createOrderTemplate, {
        onCompleted: ({ createOrder }) => {
            navigate(`/order-details/${createOrder._id}/main-view`);
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('orders:ORDER_CREATED') });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [saveCompletedDraft, { loading: draftLoading }] = useMutation(saveCompletedDraftTemplate, {
        onCompleted: () => {
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('orders:ORDER_SAVED') });
            navigate('/orders/drafts');
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [modifyOrder, { loading: modifyLoading }] = useMutation(modifyOrderTemplate, {
        onCompleted: ({ modifyOrder }) => {
            navigate(`/order-details/${modifyOrder._id}/main-view`);
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('orders:ORDER_MODIFIED') });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    // The flow saves a draft from its first step on, so sending completes that draft.
    const sendOrder = () =>
        createOrder({
            variables: { order: actionOrderToOrderInput(order), ...(order._id ? { draftId: order._id } : {}) },
        });

    const isLoginUserDentist = () =>
        isTreatingClinicianTitle(currentUser?.title, getTreatingClinicianTitleWords(currentOrg.customerSettings || []));

    const checkIfSendBtnShouldBeShown = () => {
        const baseCondition = order.status === OrderStatus.DRAFT || order.inputType === OrderInputType.NEW;
        const isLoginUserStudent = !isLoginUserDentist();
        const isStudentAndSameDentist =
            isLoginUserStudent && order.dentist?._id?.toString() === currentUser._id?.toString();

        const canShowCompletedDraft =
            baseCondition && order.dentist?._id && !isStudentAndSameDentist && order.status === OrderStatus.DRAFT;

        const canSendOrderIfDentist = !isLoginUserStudent;

        // Case 1: Completed draft enabled & non-dentists NOT allowed
        if (isCompletedDraftEnabled && !shouldDisallowNonDentists) {
            return {
                showCompletedDraft: canShowCompletedDraft,
                showSendOrder: baseCondition,
            };
        }

        // Case 2: Completed draft enabled & non-dentists allowed
        if (isCompletedDraftEnabled && shouldDisallowNonDentists) {
            return {
                showCompletedDraft: canShowCompletedDraft,
                showSendOrder: baseCondition && canSendOrderIfDentist,
            };
        }
        // Case 3: completed draft disabled
        return {
            showCompletedDraft: false,
            showSendOrder: baseCondition,
        };
    };

    const getActionButton = () => {
        switch (order.inputType) {
            case OrderInputType.MODIFY:
                return (
                    <LoadingButton
                        loading={modifyLoading}
                        disabled={modifyLoading}
                        type="button"
                        onClick={() =>
                            modifyOrder({
                                variables: { orderId: order._id, order: actionOrderToOrderInput(order), isResend },
                            })
                        }
                        variant="contained"
                        data-test="confirm-step.modify-submit"
                    >
                        {translate('orders:UPDATE_ORDER')}
                    </LoadingButton>
                );
            case OrderInputType.NEW:
                return (
                    <>
                        {checkIfSendBtnShouldBeShown().showCompletedDraft && (
                            <LoadingButton
                                loading={draftLoading}
                                disabled={draftLoading}
                                type="button"
                                onClick={() =>
                                    saveCompletedDraft({
                                        variables: {
                                            orderId: order._id,
                                        },
                                    })
                                }
                                sx={{ mr: 2 }}
                                variant="contained"
                                data-test="confirm-step.draft-submit"
                            >
                                {translate('orders:COMPLETE_DRAFT')}
                            </LoadingButton>
                        )}
                        {checkIfSendBtnShouldBeShown().showSendOrder && (
                            <LoadingButton
                                loading={loading}
                                disabled={loading}
                                type="button"
                                onClick={sendOrder}
                                variant="contained"
                                data-test="confirm-step.create-submit"
                            >
                                {translate('orders:REGISTER_ORDER')}
                            </LoadingButton>
                        )}
                    </>
                );
            case OrderInputType.WARRANTY:
                return (
                    <LoadingButton
                        loading={loading}
                        disabled={loading}
                        type="button"
                        onClick={sendOrder}
                        variant="contained"
                        data-test="confirm-step.warranty-submit"
                    >
                        {translate('orders:REGISTER_ORDER')}
                    </LoadingButton>
                );
            default:
                return null;
        }
    };

    return (
        <Fragment>
            {order ? (
                <Box>
                    <Box mb={2}>
                        <Typography variant="h1">{translate('orders:CONFIRM')}</Typography>
                    </Box>
                    <Box mb={2}>
                        <Divider />
                    </Box>
                    <Box mb={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ height: '100%' }}>
                                    <Box p={2}>
                                        <Typography variant="h4">{translate('orders:PATIENT')}</Typography>
                                        <Box mt={1}>
                                            <PatientSummary patient={order.patient} />
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ height: '100%' }}>
                                    <Box p={2}>
                                        <Typography variant="h4">{translate('orders:DENTIST')}</Typography>
                                        <Box mt={1}>
                                            <DentistSummary dentist={order.dentist} clinic={order.customer} />
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box>
                        <TransitionGroup component={null}>
                            {order.constructions.map((construction, index) => (
                                <Collapse key={index}>
                                    <Box mb={2} sx={{ display: 'flex' }}>
                                        <ConstructionBox
                                            construction={construction}
                                            onDeleteClick={() => null}
                                            onModifyClick={() => null}
                                            disableDelete={true}
                                            disableModify={true}
                                        />
                                    </Box>
                                </Collapse>
                            ))}
                        </TransitionGroup>
                    </Box>
                    {order.comment && (
                        <Box mb={2}>
                            <Paper variant="outlined">
                                <Box p={2}>
                                    <Typography variant="h4">{translate('orders:COMMENT')}</Typography>
                                    <Box mt={1}>
                                        <Box
                                            dangerouslySetInnerHTML={{
                                                __html: (order.comment || '')
                                                    // convert \n into <br/>
                                                    .replace(/\n/g, '<br/>'),
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>
                    )}
                    <Box mb={2}>
                        <Paper variant="outlined">
                            <Box p={2}>
                                <Typography variant="h4">{translate('client-directory:SUPPLIER')}</Typography>
                                <Box mt={1}>
                                    <SupplierSummary
                                        supplier={order.supplier}
                                        exceptionReasons={Object.keys(order.labExceptions || {}).filter(
                                            (key) => order.labExceptions?.[key],
                                        )}
                                    />
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                    <Box mb={2}>
                        <Paper variant="outlined">
                            <Box p={2}>
                                <Typography variant="h4">{translate('orders:DELIVERY')}</Typography>
                                <Box mt={1}>
                                    <DeliverySummary deliveryDetails={order.deliveryDetails} />
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                    <Box mb={2}>
                        <Paper variant="outlined">
                            <Box p={2}>
                                <Typography variant="h4">{translate('orders:SAMPLE_AND_PACKAGE_CONTENT')}</Typography>
                                <Box mt={1}>
                                    <SampleSummary sample={order.sample} />
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                    <Box mb={2}>
                        <Paper variant="outlined">
                            <Box p={2}>
                                <PriceSpecificationSection
                                    restorations={order.pricedRestorations || []}
                                    totalPrice={order.totalPrice || 0}
                                    currency={order.currency || ''}
                                />
                            </Box>
                        </Paper>
                    </Box>
                    <Box mb={4}>
                        <FileGrid files={order.files} />
                    </Box>
                    <Box display="flex" justifyContent="center" mb={4}>
                        <Box flexGrow={1} textAlign="left">
                            <Button type="button" onClick={onBackClick}>
                                {translate('common:BACK')}
                            </Button>
                        </Box>
                        <Box>{getActionButton()}</Box>
                    </Box>
                    <Box display="flex" justifyContent="center" maxWidth={900} margin="auto">
                        <Typography variant="h6" color="textSecondary" textAlign="center">
                            {translate('orders:ORDER_LEGAL_TEXT')}
                        </Typography>
                    </Box>
                </Box>
            ) : undefined}
        </Fragment>
    );
};

export default ConfirmStep;
